import React, { useState, useEffect, useCallback } from 'react';
import { getTrackingStats, getTrackingLogs } from '../services/api';
import Chart from 'react-apexcharts';
import {
    FiClock, FiFilter, FiUser, FiEdit, FiPlus, FiTrash2,
    FiEye, FiRefreshCw, FiChevronDown, FiChevronUp, FiBarChart2,
    FiList, FiDownload, FiSearch, FiX, FiLogIn, FiActivity
} from 'react-icons/fi';

const actionIcons: Record<string, React.ReactNode> = {
    create: <FiPlus size={13} />,
    update: <FiEdit size={13} />,
    delete: <FiTrash2 size={13} />,
    read: <FiEye size={13} />,
    login: <FiLogIn size={13} />,
};

const actionColors: Record<string, string> = {
    create: '#16A34A',
    update: '#3B82F6',
    delete: '#EF4444',
    read: '#8B5CF6',
    login: '#F59E0B',
};

const actionLabels: Record<string, string> = {
    create: 'Creation',
    update: 'Modification',
    delete: 'Suppression',
    read: 'Consultation',
    login: 'Connexion',
};

const moduleLabels: Record<string, string> = {
    auth: 'Authentification',
    activites: 'Activites',
    clients: 'Clients',
    commandes: 'Commandes',
    objectifs: 'Objectifs',
    planning: 'Planning',
    bonus: 'Bonus',
    absences: 'Absences',
    produits: 'Produits',
    users: 'Utilisateurs',
    utilisateurs: 'Utilisateurs',
    geofencing: 'Geofencing',
};

const MODULES = ['auth', 'activites', 'clients', 'commandes', 'objectifs', 'planning', 'bonus', 'absences', 'produits', 'users', 'geofencing'];
const ACTIONS = ['create', 'update', 'delete', 'read', 'login'];

const Historique: React.FC = () => {
    const [tab, setTab] = useState<'logs' | 'stats'>('logs');
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [filterModule, setFilterModule] = useState('');
    const [filterAction, setFilterAction] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [searchText, setSearchText] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [stats, setStats] = useState<any>(null);

    const limit = 40;
    const hasActiveFilters = filterModule || filterAction || filterStatus || searchText || dateDebut || dateFin;

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = { page, limit };
            if (filterModule) params.module = filterModule;
            if (filterAction) params.action = filterAction;
            if (filterStatus) params.status = filterStatus;
            if (searchText) params.search = searchText;
            if (dateDebut) params.date_debut = dateDebut;
            if (dateFin) params.date_fin = dateFin;
            const res = await getTrackingLogs(params);
            const d = res.data;
            setLogs(d.data || d.logs || []);
            setTotal(d.total || 0);
        } catch { setLogs([]); }
        setLoading(false);
    }, [page, filterModule, filterAction, filterStatus, searchText, dateDebut, dateFin]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    useEffect(() => {
        getTrackingStats().then(res => setStats(res.data)).catch(() => {});
    }, []);

    const clearFilters = () => {
        setFilterModule(''); setFilterAction(''); setFilterStatus('');
        setSearchText(''); setDateDebut(''); setDateFin('');
        setPage(1);
    };

    const formatDate = (d: string) => {
        try {
            const date = new Date(d);
            return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) +
                ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        } catch { return d; }
    };

    const renderJsonDiff = (oldVals: any, newVals: any) => {
        const old = typeof oldVals === 'string' ? (() => { try { return JSON.parse(oldVals || '{}'); } catch { return {}; } })() : (oldVals || {});
        const nw = typeof newVals === 'string' ? (() => { try { return JSON.parse(newVals || '{}'); } catch { return {}; } })() : (newVals || {});
        const allKeys = Array.from(new Set([...Object.keys(old), ...Object.keys(nw)]));
        const changedKeys = allKeys.filter(k => JSON.stringify(old[k]) !== JSON.stringify(nw[k]));

        if (changedKeys.length === 0 && allKeys.length === 0) {
            return <span style={{ color: '#94A3B8', fontSize: 12, fontStyle: 'italic' }}>Aucun detail disponible</span>;
        }

        return (
            <div style={{ fontSize: 12, background: '#F8FAFC', borderRadius: 10, padding: 14, marginTop: 8, border: '1px solid #E2E8F0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                            <th style={{ textAlign: 'left', padding: '6px 10px', color: '#64748B', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Champ</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', color: '#EF4444', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Avant</th>
                            <th style={{ textAlign: 'left', padding: '6px 10px', color: '#16A34A', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>Apres</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(changedKeys.length > 0 ? changedKeys : allKeys).map(key => (
                            <tr key={key} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                <td style={{ padding: '5px 10px', fontWeight: 600, color: '#374151' }}>{key}</td>
                                <td style={{ padding: '5px 10px', color: '#EF4444', textDecoration: old[key] !== undefined ? 'line-through' : 'none', opacity: old[key] !== undefined ? 1 : 0.4 }}>
                                    {old[key] !== undefined ? String(old[key]) : '--'}
                                </td>
                                <td style={{ padding: '5px 10px', color: '#16A34A', fontWeight: 500 }}>
                                    {nw[key] !== undefined ? String(nw[key]) : '--'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const exportCSV = () => {
        const headers = ['Date', 'Utilisateur', 'Email', 'Action', 'Module', 'Ressource', 'Statut', 'IP'];
        const rows = logs.map((l: any) => [
            formatDate(l.created_at), l.user_nom || '', l.user_email || '',
            actionLabels[l.action] || l.action, moduleLabels[l.module] || l.module,
            l.resource_id || '', l.status || '', l.ip_address || ''
        ]);
        const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const totalPages = Math.ceil(total / limit);

    // Chart options
    const moduleOpts: ApexCharts.ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif' },
        plotOptions: { bar: { borderRadius: 6, columnWidth: '55%' } },
        colors: ['#16A34A'],
        xaxis: { categories: stats?.par_module?.map((m: any) => moduleLabels[m.module] || m.module) || [] },
        yaxis: { labels: { formatter: (v: number) => Math.round(v).toString() } },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v: number) => `${v} actions` } },
    };
    const moduleSeries = [{ name: 'Actions', data: stats?.par_module?.map((m: any) => m.total || 0) || [] }];

    const jourOpts: ApexCharts.ApexOptions = {
        chart: { type: 'area', toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif' },
        colors: ['#3B82F6'],
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 } },
        xaxis: {
            categories: stats?.par_jour?.map((d: any) => d.date) || [],
            labels: { formatter: (d: string) => { try { return new Date(d).toLocaleDateString('fr', { day: '2-digit', month: 'short' }); } catch { return d; } } }
        },
        yaxis: { labels: { formatter: (v: number) => Math.round(v).toString() } },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v: number) => `${v} actions` } },
    };
    const jourSeries = [{ name: 'Actions', data: stats?.par_jour?.map((d: any) => d.total || 0) || [] }];

    const heureOpts: ApexCharts.ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans, sans-serif' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        colors: ['#8B5CF6'],
        xaxis: { categories: stats?.par_heure?.map((h: any) => `${h.heure}h`) || [] },
        yaxis: { labels: { formatter: (v: number) => Math.round(v).toString() } },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v: number) => `${v} actions` } },
    };
    const heureSeries = [{ name: 'Actions', data: stats?.par_heure?.map((h: any) => h.total || 0) || [] }];

    return (
        <div style={{ padding: 0 }}>
            {/* Header */}
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FiActivity size={22} /> Historique & Audit
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: 14 }}>
                        Suivi complet de toutes les actions du systeme
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    {tab === 'logs' && (
                        <button className="btn btn-secondary" onClick={exportCSV} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FiDownload size={16} /> Export CSV
                        </button>
                    )}
                    <button className="btn btn-secondary" onClick={() => { setPage(1); fetchLogs(); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FiRefreshCw size={16} />
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div style={{
                display: 'flex', gap: 4, padding: 4, background: '#F1F5F9',
                borderRadius: 12, marginBottom: 20, width: 'fit-content'
            }}>
                <button
                    onClick={() => setTab('logs')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
                        borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        background: tab === 'logs' ? '#fff' : 'transparent',
                        color: tab === 'logs' ? '#1E293B' : '#64748B',
                        boxShadow: tab === 'logs' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                    }}
                >
                    <FiList size={16} /> Journal ({total})
                </button>
                <button
                    onClick={() => setTab('stats')}
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px',
                        borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        background: tab === 'stats' ? '#fff' : 'transparent',
                        color: tab === 'stats' ? '#1E293B' : '#64748B',
                        boxShadow: tab === 'stats' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
                    }}
                >
                    <FiBarChart2 size={16} /> Statistiques
                </button>
            </div>

            {/* ===== LOGS TAB ===== */}
            {tab === 'logs' && (
                <>
                    {/* Search + filters bar */}
                    <div style={{
                        background: '#fff', borderRadius: 'var(--r-md)', padding: '16px 20px', marginBottom: 16,
                        boxShadow: 'var(--sh-card)', border: '1px solid var(--border)'
                    }}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: showFilters ? 16 : 0 }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <FiSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
                                <input
                                    type="text" placeholder="Rechercher par utilisateur, module..."
                                    value={searchText} onChange={e => { setSearchText(e.target.value); setPage(1); }}
                                    style={{
                                        width: '100%', padding: '10px 14px 10px 42px', border: '1px solid var(--border)',
                                        borderRadius: 'var(--r-sm)', fontSize: 14
                                    }}
                                />
                            </div>
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px',
                                    borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
                                    background: hasActiveFilters ? '#16A34A' : '#fff',
                                    color: hasActiveFilters ? '#fff' : '#64748B', fontWeight: 600, fontSize: 13, cursor: 'pointer'
                                }}
                            >
                                <FiFilter size={16} /> Filtres {hasActiveFilters && '(actifs)'}
                            </button>
                        </div>

                        {showFilters && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, alignItems: 'end' }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Module</label>
                                    <select value={filterModule} onChange={e => { setFilterModule(e.target.value); setPage(1); }}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 13 }}>
                                        <option value="">Tous</option>
                                        {MODULES.map(m => <option key={m} value={m}>{moduleLabels[m] || m}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Action</label>
                                    <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(1); }}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 13 }}>
                                        <option value="">Toutes</option>
                                        {ACTIONS.map(a => <option key={a} value={a}>{actionLabels[a] || a}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Statut</label>
                                    <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 13 }}>
                                        <option value="">Tous</option>
                                        <option value="success">Succes</option>
                                        <option value="error">Erreur</option>
                                    </select>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Debut</label>
                                        <input type="date" value={dateDebut} onChange={e => { setDateDebut(e.target.value); setPage(1); }}
                                            style={{ width: '100%', padding: '8px 6px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 12 }} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4, display: 'block' }}>Fin</label>
                                        <input type="date" value={dateFin} onChange={e => { setDateFin(e.target.value); setPage(1); }}
                                            style={{ width: '100%', padding: '8px 6px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', fontSize: 12 }} />
                                    </div>
                                </div>
                                <div>
                                    {hasActiveFilters && (
                                        <button onClick={clearFilters} style={{
                                            padding: '8px 14px', borderRadius: 'var(--r-sm)', border: 'none',
                                            background: '#FEE2E2', color: '#EF4444', fontWeight: 600, fontSize: 13,
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                                        }}>
                                            <FiX size={14} /> Effacer
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action pills */}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                        <button onClick={() => { setFilterAction(''); setPage(1); }}
                            style={{
                                padding: '6px 16px', borderRadius: 20, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                background: !filterAction ? '#1E293B' : '#F1F5F9', color: !filterAction ? '#fff' : '#64748B'
                            }}>Toutes</button>
                        {ACTIONS.map(a => {
                            const isActive = filterAction === a;
                            const color = actionColors[a] || '#94A3B8';
                            return (
                                <button key={a} onClick={() => { setFilterAction(isActive ? '' : a); setPage(1); }}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                                        borderRadius: 20, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                        background: isActive ? color : `${color}15`,
                                        color: isActive ? '#fff' : color
                                    }}>
                                    {actionIcons[a]} {actionLabels[a] || a}
                                </button>
                            );
                        })}
                    </div>

                    {/* Table */}
                    <div style={{
                        background: '#fff', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-card)',
                        border: '1px solid var(--border)', overflow: 'hidden'
                    }}>
                        {loading ? (
                            <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>
                                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                                Chargement...
                            </div>
                        ) : logs.length === 0 ? (
                            <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>
                                <FiClock size={40} style={{ marginBottom: 12 }} />
                                <p style={{ fontWeight: 600 }}>Aucun log trouve</p>
                                {hasActiveFilters && (
                                    <button onClick={clearFilters} style={{
                                        marginTop: 10, padding: '8px 20px', borderRadius: 8, border: 'none',
                                        background: '#16A34A', color: '#fff', fontWeight: 600, cursor: 'pointer'
                                    }}>Effacer les filtres</button>
                                )}
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                                        <th style={{ padding: '12px 16px', width: 36 }}></th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Date</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Utilisateur</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Action</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Module</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Ressource</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>Statut</th>
                                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase' }}>IP</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map((log: any) => {
                                        const color = actionColors[log.action] || '#94A3B8';
                                        const isExpanded = expandedId === log.id;
                                        const isError = log.status === 'error';
                                        return (
                                            <React.Fragment key={log.id}>
                                                <tr
                                                    onClick={() => setExpandedId(isExpanded ? null : log.id)}
                                                    style={{
                                                        borderBottom: isExpanded ? 'none' : '1px solid #F1F5F9',
                                                        cursor: 'pointer', transition: 'background 0.15s',
                                                        background: isExpanded ? '#FAFBFC' : isError ? '#FEF2F2' : '#fff'
                                                    }}
                                                    onMouseEnter={e => (e.currentTarget.style.background = '#F8FAFC')}
                                                    onMouseLeave={e => (e.currentTarget.style.background = isExpanded ? '#FAFBFC' : isError ? '#FEF2F2' : '#fff')}
                                                >
                                                    <td style={{ padding: '12px 16px', color: '#94A3B8' }}>
                                                        {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#64748B', whiteSpace: 'nowrap' }}>
                                                        {formatDate(log.created_at)}
                                                    </td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <div style={{
                                                                width: 30, height: 30, borderRadius: 8,
                                                                background: '#F1F5F9', display: 'flex',
                                                                alignItems: 'center', justifyContent: 'center',
                                                                color: '#64748B', fontSize: 12, fontWeight: 700
                                                            }}>
                                                                <FiUser size={14} />
                                                            </div>
                                                            <div>
                                                                <div style={{ fontSize: 13, fontWeight: 600 }}>{log.user_nom || '--'}</div>
                                                                {log.user_email && <div style={{ fontSize: 11, color: '#94A3B8' }}>{log.user_email}</div>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center', gap: 4,
                                                            padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                                            color, background: `${color}12`
                                                        }}>
                                                            {actionIcons[log.action] || <FiEdit size={13} />}
                                                            {actionLabels[log.action] || log.action}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        <span style={{
                                                            padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                            background: '#F1F5F9', color: '#475569'
                                                        }}>
                                                            {moduleLabels[log.module] || log.module}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 12, color: '#64748B' }}>
                                                        {log.resource_id ? `#${log.resource_id}` : '--'}
                                                    </td>
                                                    <td style={{ padding: '12px 16px' }}>
                                                        {log.status && (
                                                            <span style={{
                                                                padding: '3px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                                                                textTransform: 'uppercase',
                                                                background: isError ? '#FEE2E2' : '#F0FDF4',
                                                                color: isError ? '#DC2626' : '#16A34A'
                                                            }}>
                                                                {log.status}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px 16px', fontSize: 11, color: '#94A3B8' }}>
                                                        {log.ip_address || '--'}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan={8} style={{ padding: '0 20px 16px', borderBottom: '1px solid #E2E8F0', background: '#FAFBFC' }}>
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
                                                                <div>
                                                                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <FiEdit size={14} /> Modifications
                                                                    </h4>
                                                                    {renderJsonDiff(log.old_values, log.new_values)}
                                                                </div>
                                                                <div>
                                                                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                                                        <FiEye size={14} /> Informations
                                                                    </h4>
                                                                    <div style={{ fontSize: 12, background: '#F8FAFC', borderRadius: 10, padding: 14, border: '1px solid #E2E8F0' }}>
                                                                        <div style={{ display: 'grid', gap: 8 }}>
                                                                            {log.user_agent && (
                                                                                <div>
                                                                                    <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>User Agent</div>
                                                                                    <div style={{ fontSize: 12, color: '#374151', wordBreak: 'break-all' }}>{log.user_agent}</div>
                                                                                </div>
                                                                            )}
                                                                            <div>
                                                                                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>Date complete</div>
                                                                                <div style={{ fontSize: 12, color: '#374151' }}>{new Date(log.created_at).toLocaleString('fr-FR')}</div>
                                                                            </div>
                                                                            <div>
                                                                                <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>ID Log</div>
                                                                                <div style={{ fontSize: 12, color: '#374151', fontFamily: 'monospace' }}>#{log.id}</div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '14px 20px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC'
                            }}>
                                <span style={{ fontSize: 13, color: '#64748B' }}>
                                    Page {page} sur {totalPages} ({total} resultats)
                                </span>
                                <div style={{ display: 'flex', gap: 6 }}>
                                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                                        className="btn btn-secondary" style={{ fontSize: 13, padding: '6px 14px' }}>
                                        Precedent
                                    </button>
                                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                                        className="btn btn-secondary" style={{ fontSize: 13, padding: '6px 14px' }}>
                                        Suivant
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ===== STATS TAB ===== */}
            {tab === 'stats' && (
                <>
                    {!stats ? (
                        <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            Chargement des statistiques...
                        </div>
                    ) : (
                        <div className="charts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                            <div style={{ background: '#fff', borderRadius: 'var(--r-md)', padding: 24, boxShadow: 'var(--sh-card)', border: '1px solid var(--border)' }}>
                                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Actions par module</h3>
                                <Chart options={moduleOpts} series={moduleSeries} type="bar" height={300} />
                            </div>
                            <div style={{ background: '#fff', borderRadius: 'var(--r-md)', padding: 24, boxShadow: 'var(--sh-card)', border: '1px solid var(--border)' }}>
                                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Activite par jour</h3>
                                <Chart options={jourOpts} series={jourSeries} type="area" height={300} />
                            </div>
                            <div style={{ background: '#fff', borderRadius: 'var(--r-md)', padding: 24, boxShadow: 'var(--sh-card)', border: '1px solid var(--border)' }}>
                                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Top utilisateurs</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {stats.top_users?.map((u: any, i: number) => (
                                        <div key={i} style={{
                                            display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                                            background: '#F8FAFC', borderRadius: 10, border: '1px solid #F1F5F9'
                                        }}>
                                            <span style={{
                                                width: 28, height: 28, borderRadius: 8,
                                                background: i < 3 ? ['#FEF3C7', '#F1F5F9', '#FFF7ED'][i] : '#F8FAFC',
                                                color: i < 3 ? ['#B45309', '#475569', '#C2410C'][i] : '#94A3B8',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 13, fontWeight: 800
                                            }}>{i + 1}</span>
                                            <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#1E293B' }}>{u.user_nom}</span>
                                            <span style={{
                                                padding: '3px 10px', borderRadius: 20, background: '#F0FDF4',
                                                color: '#16A34A', fontSize: 13, fontWeight: 700
                                            }}>{u.total}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div style={{ background: '#fff', borderRadius: 'var(--r-md)', padding: 24, boxShadow: 'var(--sh-card)', border: '1px solid var(--border)' }}>
                                <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: '#1E293B' }}>Activite par heure</h3>
                                <Chart options={heureOpts} series={heureSeries} type="bar" height={300} />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Historique;
